import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import EventsService from "../services/EventsService";
import { getSocket } from "../socket";

const PLAYER_COLORS = [
  "#0A84FF", "#30D158", "#FF453A", "#FFD60A",
  "#BF5AF2", "#FF9F0A", "#64D2FF", "#FF375F",
  "#AC8E68", "#5E5CE6", "#34C759", "#FF6B6B",
];

function SharedEventPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [timeArray, setTimeArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      const ev = await EventsService.getSharedEvent(id);
      setEvent(ev);
    } catch {}
  }, [id]);

  const fetchTimeArray = useCallback(async () => {
    try {
      const ta = await EventsService.getSharedTimeArray(id);
      setTimeArray(ta || []);
    } catch {}
  }, [id]);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchEvent(), fetchTimeArray()]);
      setLoading(false);
    };
    load();
  }, [fetchEvent, fetchTimeArray]);

  useEffect(() => {
    let socket = null;
    let mounted = true;
    const setup = async () => {
      try {
        socket = await getSocket();
        socket.on("sharedEvent:timepointAdded", (payload) => {
          if (!mounted || payload.sharedEventId !== id) return;
          fetchTimeArray();
        });
        socket.on("sharedEvent:participantJoined", (payload) => {
          if (!mounted || payload.sharedEventId !== id) return;
          fetchEvent();
        });
      } catch {}
    };
    setup();
    return () => {
      mounted = false;
      if (socket) {
        socket.off("sharedEvent:timepointAdded");
        socket.off("sharedEvent:participantJoined");
      }
    };
  }, [id, fetchTimeArray, fetchEvent]);

  const handleAction = async (actionId) => {
    if (!event || event.finished) return;
    try {
      await EventsService.addSharedTimepoint(id, actionId);
      fetchTimeArray();
    } catch {}
  };

  const handleStart = async () => {
    try {
      await EventsService.startSharedEvent(id);
      fetchEvent();
    } catch {}
  };

  const handleEnd = async () => {
    if (!window.confirm("End this event? All participants will stop tracking.")) return;
    try {
      await EventsService.endSharedEvent(id);
      fetchEvent();
    } catch {}
  };

  const copyCode = () => {
    if (!event?.code) return;
    navigator.clipboard.writeText(event.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    if (!event?.code) return;
    navigator.clipboard.writeText(`${window.location.origin}/join/${event.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <>
        <div className="page-header">
          <nav className="navbar navbar-expand-lg d-flex justify-content-between">
            <div className="header-title flex-fill">
              <a id="sidebar-toggle" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }} href="#">
                <i className="fas fa-bars"></i>
              </a>
              <h5>Loading...</h5>
            </div>
          </nav>
        </div>
        <div className="main-wrapper" style={{ textAlign: "center", padding: 60 }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
        </div>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <div className="page-header">
          <nav className="navbar navbar-expand-lg d-flex justify-content-between">
            <div className="header-title flex-fill">
              <a id="sidebar-toggle" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }} href="#">
                <i className="fas fa-bars"></i>
              </a>
              <h5>Event not found</h5>
            </div>
          </nav>
        </div>
        <div className="main-wrapper"><p style={{ color: "rgba(255,255,255,0.4)" }}>This event doesn't exist or you don't have access.</p></div>
      </>
    );
  }

  const participants = event.participants || [];
  const actions = event.actions || [];
  const isOwner = event.isOwner;

  const playerColor = (idx) => PLAYER_COLORS[idx % PLAYER_COLORS.length];

  const getCounts = () => {
    const counts = {};
    participants.forEach((p) => { counts[p.id] = {}; actions.forEach((a) => { counts[p.id][a.id] = 0; }); });
    timeArray.forEach((tp) => {
      if (counts[tp.participantId]?.[tp.actionId] !== undefined) {
        counts[tp.participantId][tp.actionId]++;
      }
    });
    return counts;
  };

  const counts = getCounts();

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a id="sidebar-toggle" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }} href="#">
              <i className="fas fa-bars"></i>
            </a>
            <h5>{event.name}</h5>
          </div>
        </nav>
      </div>

      <div className="main-wrapper">
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {/* Invite Code Banner */}
          <div style={{
            background: "rgba(10,132,255,0.08)", border: "1px solid rgba(10,132,255,0.2)",
            borderRadius: 16, padding: "16px 20px", marginBottom: 24,
            display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between",
          }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 4 }}>
                Invitation Code
              </div>
              <div style={{
                fontSize: 28, fontWeight: 800, color: "#0A84FF", letterSpacing: "0.2em", fontFamily: "monospace",
              }}>{event.code}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={copyCode} style={{
                background: "rgba(10,132,255,0.15)", border: "none", borderRadius: 10,
                padding: "8px 16px", color: "#0A84FF", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>
                <i className={`fas ${copied ? "fa-check" : "fa-copy"}`} style={{ marginRight: 6 }}></i>
                {copied ? "Copied!" : "Copy Code"}
              </button>
              <button onClick={copyLink} style={{
                background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 10,
                padding: "8px 16px", color: "rgba(255,255,255,0.6)", fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>
                <i className="fas fa-link" style={{ marginRight: 6 }}></i>
                Copy Link
              </button>
            </div>
          </div>

          {/* Status + Controls */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{
              fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
              padding: "5px 12px", borderRadius: 8,
              background: event.finished ? "rgba(255,255,255,0.06)" : event.started ? "rgba(48,209,88,0.15)" : "rgba(255,214,10,0.15)",
              color: event.finished ? "rgba(255,255,255,0.4)" : event.started ? "#30D158" : "#FFD60A",
            }}>
              {event.finished ? "Ended" : event.started ? "Live" : "Not Started"}
            </span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
              {participants.length} participant{participants.length !== 1 ? "s" : ""} · {actions.length} action{actions.length !== 1 ? "s" : ""}
            </span>
            <div style={{ flex: 1 }} />
            {isOwner && !event.started && !event.finished && (
              <button className="btn create-event-start-btn" style={{ padding: "8px 20px", fontSize: 13 }} onClick={handleStart}>
                <i className="fas fa-play" style={{ marginRight: 6 }}></i>Start Event
              </button>
            )}
            {isOwner && event.started && !event.finished && (
              <button className="btn" style={{
                background: "rgba(255,69,58,0.15)", border: "1px solid rgba(255,69,58,0.3)",
                color: "#FF453A", borderRadius: 12, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }} onClick={handleEnd}>
                <i className="fas fa-stop" style={{ marginRight: 6 }}></i>End Event
              </button>
            )}
          </div>

          {/* Action Buttons */}
          {event.started && !event.finished && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
              {actions.map((action, i) => (
                <button
                  key={action.id}
                  className="event-action-btn"
                  style={{ "--action-color": `hsl(${(i * 60) % 360}, 65%, 45%)`, width: "auto", flex: "1 1 120px", maxHeight: 100, aspectRatio: "auto", padding: "16px 20px" }}
                  onClick={() => handleAction(action.id)}
                >
                  <span className="event-action-name">{action.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Scoreboard */}
          <div style={{ marginBottom: 28 }}>
            <h6 className="event-section-title">Scoreboard</h6>
            <div style={{
              background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
              border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16,
              overflow: "hidden",
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Player</th>
                    {actions.map((a) => (
                      <th key={a.id} style={{ padding: "12px 16px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{a.name}</th>
                    ))}
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p, pi) => {
                    const total = actions.reduce((sum, a) => sum + (counts[p.id]?.[a.id] || 0), 0);
                    return (
                      <tr key={p.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: "50%",
                              background: p.color || playerColor(pi),
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12, fontWeight: 700, color: "#fff",
                            }}>{p.displayName?.charAt(0)?.toUpperCase() || "?"}</div>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{p.displayName}</span>
                          </div>
                        </td>
                        {actions.map((a) => (
                          <td key={a.id} style={{ padding: "12px 16px", textAlign: "center", fontSize: 18, fontWeight: 700 }}>
                            {counts[p.id]?.[a.id] || 0}
                          </td>
                        ))}
                        <td style={{ padding: "12px 16px", textAlign: "center", fontSize: 18, fontWeight: 800, color: "#0A84FF" }}>
                          {total}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity Feed */}
          <div>
            <h6 className="event-section-title">Activity Feed</h6>
            <div style={{
              background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
              border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16,
              padding: "12px 16px", maxHeight: 400, overflowY: "auto",
            }}>
              {timeArray.length === 0 ? (
                <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, textAlign: "center", margin: "20px 0" }}>
                  No activity yet. {!event.started ? "Start the event to begin tracking." : "Tap an action button to log."}
                </p>
              ) : (
                [...timeArray].reverse().slice(0, 100).map((tp, i) => {
                  const participant = participants.find((p) => p.id === tp.participantId);
                  const action = actions.find((a) => a.id === tp.actionId);
                  const pi = participants.findIndex((p) => p.id === tp.participantId);
                  return (
                    <div key={tp.id || i} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
                      borderBottom: i < timeArray.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: participant?.color || playerColor(pi),
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0,
                      }}>{participant?.displayName?.charAt(0)?.toUpperCase() || "?"}</div>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{participant?.displayName || "Unknown"}</span>
                      <span style={{
                        background: "rgba(255,255,255,0.06)", borderRadius: 6,
                        padding: "2px 8px", fontSize: 11, fontWeight: 600,
                        color: "rgba(255,255,255,0.6)",
                      }}>{action?.name || "?"}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
                        {new Date(tp.time).toLocaleTimeString()}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Participants */}
          {event.description && (
            <div style={{ marginTop: 24 }}>
              <h6 className="event-section-title">Description</h6>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, lineHeight: 1.6 }}>{event.description}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SharedEventPage;
